import * as THREE from 'three';
import type { Character, Side } from '../data/types';
import { getAbility, getAbilityForCharacter } from './abilities';
import { spriteUrl } from '../data/api';

export type StatusKey = 'stun' | 'slow' | 'haste' | 'taunt' | 'buffed' | 'frozen' | 'burning';

export class Unit {
  pos: THREE.Vector3;
  knockVel = new THREE.Vector3();
  hp: number;
  maxHp: number;
  dead = false;
  attackCd = 0;
  abilityCd = 2 + Math.random() * 4;
  status: Partial<Record<StatusKey, number>> = {};
  group: THREE.Group;
  sprite: THREE.Sprite;
  hpBarBg!: THREE.Sprite;
  hpBarFill!: THREE.Sprite;
  hpBarFillMat!: THREE.SpriteMaterial;
  baseScale = 2.4;

  // Animation state
  idleT = Math.random() * Math.PI * 2;
  walkT = 0;
  isWalking = false;
  attackAnimT = 0;        // counts down during attack lunge
  attackAnimDur = 0.35;
  hitFlashT = 0;          // counts down for white-flash on hit
  deathT = 0;             // counts up after death
  spawnT = 0;             // counts up from spawn for pop-in

  // Per-faction animation flavour
  bobMag: number;
  bobSpeed: number;

  // Status FX meshes (created on demand, removed when status ends)
  iceFx: THREE.Mesh | null = null;
  fireFx: THREE.Group | null = null;
  shieldFx: THREE.Mesh | null = null;
  buffFx: THREE.Mesh | null = null;
  lastHitCrit = false;
  lastStandFx: THREE.Mesh | null = null;

  constructor(public character: Character, public side: Side, startPos: THREE.Vector3, texture: THREE.Texture) {
    this.maxHp = character.hp * 25;
    this.hp = this.maxHp;
    this.pos = startPos.clone();
    this.group = new THREE.Group();
    this.group.position.copy(this.pos);

    // Tune idle animation per faction so each type "feels" different.
    if (character.faction === 'pokemon') { this.bobMag = 0.18; this.bobSpeed = 5.5; }
    else if (character.faction === 'plant') { this.bobMag = 0.10; this.bobSpeed = 2.5; }
    else { this.bobMag = 0.14; this.bobSpeed = 3.0; }

    // ground shadow (dark)
    const shadowGeo = new THREE.CircleGeometry(0.9, 20);
    const shadowMat = new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.45 });
    const shadow = new THREE.Mesh(shadowGeo, shadowMat);
    shadow.rotation.x = -Math.PI / 2;
    shadow.position.y = 0.02;
    this.group.add(shadow);

    // Team-colored base ring so it's instantly obvious which side a unit is on.
    const teamColor = side === 'left' ? 0x4dd0ff : 0xff5277;
    const ringGeo = new THREE.RingGeometry(1.0, 1.25, 32);
    const ringMat = new THREE.MeshBasicMaterial({
      color: teamColor, transparent: true, opacity: 0.95, side: THREE.DoubleSide,
    });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.04;
    this.group.add(ring);

    // Soft team-colored glow disc just inside the ring
    const discGeo = new THREE.CircleGeometry(1.0, 24);
    const discMat = new THREE.MeshBasicMaterial({
      color: teamColor, transparent: true, opacity: 0.25, side: THREE.DoubleSide,
    });
    const disc = new THREE.Mesh(discGeo, discMat);
    disc.rotation.x = -Math.PI / 2;
    disc.position.y = 0.03;
    this.group.add(disc);

    // sprite (billboarded)
    const mat = new THREE.SpriteMaterial({ map: texture, transparent: true, alphaTest: 0.05 });
    this.sprite = new THREE.Sprite(mat);
    this.sprite.scale.set(this.baseScale * (side === 'right' ? -1 : 1), this.baseScale, 1);
    this.sprite.position.y = this.baseScale / 2 + 0.05;
    this.group.add(this.sprite);

    // hp bar
    const bgMat = new THREE.SpriteMaterial({ color: 0x000000, transparent: true, opacity: 0.7, depthTest: false });
    this.hpBarBg = new THREE.Sprite(bgMat);
    this.hpBarBg.scale.set(1.8, 0.22, 1);
    this.hpBarBg.position.y = this.baseScale + 0.55;
    this.group.add(this.hpBarBg);

    this.hpBarFillMat = new THREE.SpriteMaterial({
      color: side === 'left' ? 0x4dd0ff : 0xff5277,
      transparent: true,
      depthTest: false,
    });
    this.hpBarFill = new THREE.Sprite(this.hpBarFillMat);
    this.hpBarFill.scale.set(1.7, 0.14, 1);
    this.hpBarFill.position.y = this.baseScale + 0.55;
    this.hpBarFill.position.z = 0.01;
    this.group.add(this.hpBarFill);
  }

  distanceTo(o: Unit): number { return this.pos.distanceTo(o.pos); }

  updateStatusFx(dt: number) {
    // Frozen: chunky translucent ice cube around the unit
    if (this.status.frozen && !this.iceFx) {
      const geo = new THREE.BoxGeometry(2.0, 2.6, 2.0);
      const mat = new THREE.MeshPhongMaterial({
        color: 0xaaddff, transparent: true, opacity: 0.55,
        shininess: 120, specular: 0xffffff,
      });
      const cube = new THREE.Mesh(geo, mat);
      cube.position.y = 1.4;
      this.group.add(cube);
      // wireframe overlay for crystal facets
      const wire = new THREE.Mesh(
        new THREE.BoxGeometry(2.05, 2.65, 2.05),
        new THREE.MeshBasicMaterial({ color: 0xffffff, wireframe: true, transparent: true, opacity: 0.6 })
      );
      cube.add(wire);
      this.iceFx = cube;
    } else if (!this.status.frozen && this.iceFx) {
      this.group.remove(this.iceFx);
      this.iceFx = null;
    }
    if (this.iceFx) {
      this.iceFx.rotation.y += dt * 0.6;
    }

    // Burning: orbiting flame sprites
    if (this.status.burning && !this.fireFx) {
      const g = new THREE.Group();
      for (let i = 0; i < 5; i++) {
        const mat = new THREE.SpriteMaterial({
          color: i % 2 ? 0xffaa33 : 0xff4422,
          transparent: true, opacity: 0.9, depthWrite: false,
        });
        const sp = new THREE.Sprite(mat);
        sp.scale.set(0.9, 1.2, 1);
        g.add(sp);
      }
      g.position.y = 1.0;
      this.group.add(g);
      this.fireFx = g;
    } else if (!this.status.burning && this.fireFx) {
      this.group.remove(this.fireFx);
      this.fireFx = null;
    }
    if (this.fireFx) {
      const t = performance.now() * 0.005;
      this.fireFx.children.forEach((c, i) => {
        const a = t + (i / 5) * Math.PI * 2;
        c.position.set(Math.cos(a) * 0.9, Math.sin(t * 3 + i) * 0.3 + 0.5, Math.sin(a) * 0.9);
      });
    }

    // Buffed (shield/buff): golden translucent sphere
    if (this.status.buffed && !this.shieldFx) {
      const geo = new THREE.SphereGeometry(1.5, 18, 14);
      const mat = new THREE.MeshBasicMaterial({
        color: 0xffd84d, transparent: true, opacity: 0.25,
        side: THREE.DoubleSide,
      });
      const s = new THREE.Mesh(geo, mat);
      s.position.y = 1.4;
      this.group.add(s);
      this.shieldFx = s;
    } else if (!this.status.buffed && this.shieldFx) {
      this.group.remove(this.shieldFx);
      this.shieldFx = null;
    }
    if (this.shieldFx) {
      const p = 1 + Math.sin(performance.now() * 0.005) * 0.05;
      this.shieldFx.scale.setScalar(p);
    }
  }

  takeDamage(amount: number, _type: string, ignoreDef = false, attackerPos?: THREE.Vector3, canCrit = true): number {
    if (this.dead) return 0;
    let dmg = amount;
    if (!ignoreDef) dmg = Math.max(1, amount - this.character.defense * 0.3);
    if (this.status.buffed) dmg *= 0.7;
    // ----- elemental synergy -----
    // fire vs burning → ×1.4, ice vs frozen → ×1.4, electric vs slowed → ×1.3
    if (_type === 'fire' && this.status.burning) dmg *= 1.4;
    else if (_type === 'ice' && this.status.frozen) dmg *= 1.4;
    else if (_type === 'electric' && this.status.slow) dmg *= 1.3;
    // ----- crit roll -----
    this.lastHitCrit = false;
    if (canCrit && Math.random() < 0.15) {
      dmg *= 2;
      this.lastHitCrit = true;
    }
    this.hp -= dmg;
    this.hitFlashT = 0.25;
    // knockback away from attacker
    if (attackerPos) {
      const dir = this.pos.clone().sub(attackerPos);
      dir.y = 0;
      if (dir.lengthSq() < 0.01) dir.set(this.side === 'left' ? -1 : 1, 0, 0);
      dir.normalize();
      const force = Math.min(5, dmg * 0.025 + 1.8);
      this.knockVel.addScaledVector(dir, force);
    }
    if (this.hp <= 0) { this.hp = 0; this.dead = true; }
    return dmg;
  }

  heal(amount: number) { if (!this.dead) this.hp = Math.min(this.maxHp, this.hp + amount); }

  applyStatus(s: StatusKey, duration: number) {
    this.status[s] = Math.max(this.status[s] ?? 0, duration);
  }

  // Engine attaches these:
  spawnEffect(_kind: string) {}
  shootProjectile(_target: Unit, _damage: number, _type: string, _ignoreDef = false) {}

  startAttackAnim() { this.attackAnimT = this.attackAnimDur; }

  teleportNear(target: Unit) {
    const offset = new THREE.Vector3((this.side === 'left' ? -2 : 2), 0, 0);
    this.pos.copy(target.pos).add(offset);
    this.group.position.copy(this.pos);
  }

  isStunned(): boolean { return !!this.status.stun || !!this.status.frozen; }
  isFrozen(): boolean { return !!this.status.frozen; }

  speedMultiplier(): number {
    let m = 1;
    if (this.status.slow) m *= 0.4;
    if (this.status.haste) m *= 1.6;
    if (this.isStunned()) m = 0;
    return m;
  }

  /** Per-frame animation update. Called by engine. */
  animate(dt: number) {
    this.spawnT += dt;
    this.idleT += dt * this.bobSpeed;
    if (this.isWalking) this.walkT += dt * 9; else this.walkT *= 0.85;

    // knockback decay
    if (this.knockVel.lengthSq() > 0.0001) {
      this.pos.addScaledVector(this.knockVel, dt);
      this.knockVel.multiplyScalar(Math.exp(-10 * dt));
    }

    // hit flash decay
    if (this.hitFlashT > 0) this.hitFlashT = Math.max(0, this.hitFlashT - dt);
    // attack anim decay
    if (this.attackAnimT > 0) this.attackAnimT = Math.max(0, this.attackAnimT - dt);
    if (this.dead) this.deathT += dt;

    // ----- compute sprite transform -----
    const facing = this.side === 'right' ? -1 : 1;
    let sx = this.baseScale * facing;
    let sy = this.baseScale;
    let yOff = this.baseScale / 2 + 0.05;
    let xOff = 0;

    // spawn pop-in
    if (this.spawnT < 0.4) {
      const k = this.spawnT / 0.4;
      const e = 1 - Math.pow(1 - k, 3);
      sx *= e; sy *= e * (0.7 + 0.3 * e);
    }

    // idle bob (vertical sine + slight horizontal sway)
    const bob = Math.sin(this.idleT) * this.bobMag;
    yOff += bob;
    // squash & stretch from bob
    const sq = 1 + Math.sin(this.idleT * 2) * 0.04;
    sx *= sq; sy *= 2 - sq;

    // walk: bigger bob + tilt
    if (this.walkT > 0.1) {
      const wb = Math.abs(Math.sin(this.walkT)) * 0.25;
      yOff += wb;
    }

    // attack anim: wind-up squash then forward lunge then snap back
    if (this.attackAnimT > 0) {
      const k = 1 - this.attackAnimT / this.attackAnimDur; // 0..1
      // 0 → 0.4: wind back; 0.4 → 0.7: lunge forward; 0.7 → 1: settle
      let lunge = 0;
      if (k < 0.4) lunge = -0.4 * (k / 0.4);              // pull back
      else if (k < 0.7) lunge = -0.4 + 1.6 * ((k - 0.4) / 0.3); // shoot forward
      else lunge = 1.2 * (1 - (k - 0.7) / 0.3);            // settle
      xOff += lunge * facing;
      // squish during lunge
      const sm = 1 + Math.sin(k * Math.PI) * 0.18;
      sx *= sm; sy *= 2 - sm;
    }

    // hit flinch: small backward shake
    if (this.hitFlashT > 0) {
      const k = this.hitFlashT / 0.25;
      xOff += Math.sin(this.hitFlashT * 60) * 0.15 * k * facing;
    }

    // death anim: fall over + fade
    if (this.dead) {
      const k = Math.min(1, this.deathT / 0.8);
      sy *= 1 - k;
      sx *= 1 - k * 0.5;
      yOff *= 1 - k;
      const m = this.sprite.material as THREE.SpriteMaterial;
      m.opacity = 1 - k;
      this.hpBarBg.material.opacity = (1 - k) * 0.7;
      this.hpBarFillMat.opacity = 1 - k;
      if (k >= 1) this.group.visible = false;
    }

    this.sprite.scale.set(sx, sy, 1);
    this.sprite.position.set(xOff, yOff, 0);

    // ----- status FX attachments -----
    this.updateStatusFx(dt);

    // ----- color tint -----
    const mat = this.sprite.material as THREE.SpriteMaterial;
    if (this.hitFlashT > 0) {
      const k = this.hitFlashT / 0.25;
      // mix white→red
      const r = 1, g = 1 - k * 0.7, b = 1 - k * 0.7;
      mat.color.setRGB(r, g, b);
    } else if (this.isFrozen()) mat.color.setHex(0x88ccff);
    else if (this.status.burning) mat.color.setHex(0xffaa66);
    else if (this.status.buffed) mat.color.setHex(0xffe97a);
    else if (!this.dead && this.hp / this.maxHp < 0.2) {
      // Last stand: pulsing red rage tint
      const k = 0.5 + Math.sin(performance.now() * 0.012) * 0.5;
      mat.color.setRGB(1, 0.4 + k * 0.3, 0.4 + k * 0.3);
    }
    else mat.color.setHex(0xffffff);

    // ----- HP bar -----
    const ratio = this.hp / this.maxHp;
    this.hpBarFill.scale.x = 1.7 * ratio;
    this.hpBarFill.position.x = -(1.7 * (1 - ratio)) / 2;
    if (ratio > 0.6) this.hpBarFillMat.color.setHex(0x4dffa8);
    else if (ratio > 0.3) this.hpBarFillMat.color.setHex(0xffd84d);
    else this.hpBarFillMat.color.setHex(0xff5277);

    this.group.position.copy(this.pos);
  }
}

export async function loadTexture(url: string): Promise<THREE.Texture> {
  return new Promise((resolve) => {
    const loader = new THREE.TextureLoader();
    loader.crossOrigin = 'anonymous';
    loader.load(
      url,
      (tex) => {
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.minFilter = THREE.LinearFilter;
        tex.magFilter = THREE.LinearFilter;
        resolve(tex);
      },
      undefined,
      () => {
        const c = document.createElement('canvas');
        c.width = c.height = 64;
        const ctx = c.getContext('2d')!;
        ctx.fillStyle = '#888'; ctx.fillRect(0, 0, 64, 64);
        ctx.fillStyle = '#fff'; ctx.font = '12px sans-serif'; ctx.fillText('?', 26, 38);
        const tex = new THREE.CanvasTexture(c);
        resolve(tex);
      }
    );
  });
}

export async function makeUnit(c: Character, side: Side, pos: THREE.Vector3): Promise<Unit> {
  const tex = await loadTexture(spriteUrl(c));
  return new Unit(c, side, pos, tex);
}

export function getAbilityDef(c: Character) { return getAbilityForCharacter(c); }
// keep getAbility re-export so other modules don't break
export { getAbility };
